import { DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, catchError, distinctUntilChanged, finalize, map, merge, of, startWith, switchMap, tap } from 'rxjs';

import { BalanceService, IFeaturedPromotion, PromotionsService } from 'core';
import { KorviuButtonComponent, KorviuLoaderComponent, KorviuNoDataComponent } from 'shared';
import { RefreshOnLanguageChangeDirective } from 'shared/directives/refresh-on-language-change.directive';

@Component({
  selector: 'app-featured-promotions',
  imports: [KorviuButtonComponent, KorviuLoaderComponent, KorviuNoDataComponent, RouterLink, TranslatePipe, DatePipe],
  templateUrl: './featured-promotions.component.html',
  styleUrl: './featured-promotions.component.scss',
  hostDirectives: [RefreshOnLanguageChangeDirective]
})
export class FeaturedPromotionsComponent implements OnInit {
  readonly balanceService = inject(BalanceService);
  readonly promotionsService = inject(PromotionsService);
  readonly destroyRef = inject(DestroyRef);
  readonly translateService = inject(TranslateService);
  readonly #reloadPromotion$ = new Subject<void>();
  readonly #currentLanguage = toSignal(this.translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.translateService.currentLang || this.translateService.defaultLang || 'es'
  });

  readonly featuredPromotion = signal<IFeaturedPromotion | null>(null);
  readonly featuredIsLoading = signal<boolean>(false);
  readonly availableEntriesLabel = computed(() => {
    this.#currentLanguage();
    const availableEntries = this.featuredPromotion()?.available_entries;

    if (availableEntries == null) {
      return '';
    }

    return this.#getCountLabel('dashboard.availableEntries', 'dashboard.availableEntries_other', availableEntries);
  });
  readonly ticketsLabel = computed(() => {
    this.#currentLanguage();
    const tickets = this.featuredPromotion()?.tickets;

    if (tickets == null) {
      return '';
    }

    return this.#getCountLabel('dashboard.tickets', 'dashboard.tickets_other', tickets);
  });
  readonly timeRemainingLabel = computed(() => {
    this.#currentLanguage();
    const endDate = this.featuredPromotion()?.end_date;

    if (!endDate) {
      return '';
    }

    return this.#getTimeRemainingLabel(endDate);
  });

  ngOnInit(): void {
    merge(
      this.#reloadPromotion$,
      this.translateService.onLangChange.pipe(
        map(event => event.lang),
        startWith(this.translateService.currentLang || this.translateService.defaultLang || 'es'),
        distinctUntilChanged()
      )
    )
      .pipe(
        switchMap(() => {
          this.featuredPromotion.set(null);
          this.featuredIsLoading.set(true);

          return this.promotionsService.getFeaturedPromotions({ page: 1, pageSize: 10 }).pipe(
            tap(response => this.featuredPromotion.set(response.results[0] ?? null)),
            catchError(() => of(null)),
            finalize(() => this.featuredIsLoading.set(false))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  get lessThan500Ganas() {
    return this.balanceService.spendableGanas() < 500;
  }

  getFeaturedPromotion(): void {
    this.#reloadPromotion$.next();
  }

  #getTimeRemainingLabel(endDate: string): string {
    const endTimestamp = new Date(endDate).getTime();

    if (Number.isNaN(endTimestamp)) {
      return '';
    }

    const diffInMs = endTimestamp - Date.now();
    const hourInMs = 1000 * 60 * 60;
    const dayInMs = hourInMs * 24;

    if (diffInMs <= 0) {
      return this.translateService.instant('dashboard.expired');
    }

    const totalDays = Math.floor(diffInMs / dayInMs);
    if (totalDays >= 1) {
      return this.#getCountLabel('dashboard.daysLeft', 'dashboard.daysLeft_other', totalDays);
    }

    const totalHours = Math.floor(diffInMs / hourInMs);
    if (totalHours >= 1) {
      return this.#getCountLabel('dashboard.hoursLeft', 'dashboard.hoursLeft_other', totalHours);
    }

    return this.translateService.instant('dashboard.lessThanHourLeft');
  }

  #getCountLabel(singularKey: string, pluralKey: string, count: number): string {
    return this.translateService.instant(count === 1 ? singularKey : pluralKey, { count });
  }
}
